import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BsFilterCircle } from "react-icons/bs";
import { FiDelete } from "react-icons/fi";
import { LoginContext } from "helpers/LoginContext";
import SideBarNew from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import { SP_SETTING_BREADCRUMB, SETTINGS_BREADCRUMB_PAGES } from "Constants/BreadcrumbConstant";
import { STATES_LIST } from "Constants/constant";
import { apiGetRequest, apiPatchRequest, apiDeleteRequest, apiPostRequest, apiPutRequest } from "helpers/Requests";
import { URL_CUSTOM_LINE_ITEMS } from "store/services/URL";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import SelectOption from "components/common/Select/Select";
import Search from "components/common/Search/Search";
import ConfirmationModal from "components/common/Modal/ConfirmationModal";
import ServiceLineItemModal from "./ServiceLineItemModal";
import ServiceTypeModal from "./ServiceTypeModal";
import { useForm } from "react-hook-form";
import { getSpSetting } from "store/selector/sp.selector";
import { capitalizeWords, getServiceCategoryNameValidationError } from "Utils/utils";

const toDisplayLabel = (value) => {
  const label = String(value || "").trim();
  if (!label) return "";
  // Keep existing title/mixed case; only fix fully-lowercase API labels (e.g. "core")
  if (label !== label.toLowerCase()) return label;
  return capitalizeWords(label);
};

const ALL_FUNDING_TYPES = [
  { value: "NDIS", label: "NDIS" },
  { value: "AGED_CARE", label: "Aged Care" },
  { value: "PRIVATE", label: "Private" },
  { value: "OTHER", label: "Other" },
];

/**
 * Filter funding types based on care sector setting.
 * This screen only: NDIS organisations also see Aged Care.
 * Aged Care organisations stay Aged Care, Private, Other.
 */
const getAllowedFundingTypes = (careSector) => {
  if (careSector === "aged_care") {
    return ALL_FUNDING_TYPES.filter(ft =>
      ft.value === "AGED_CARE" || ft.value === "PRIVATE" || ft.value === "OTHER"
    );
  }
  // NDIS, both, or unset: NDIS and Aged Care together, plus Private and Other
  return ALL_FUNDING_TYPES;
};

/** Sentinel for react-select (avoids empty-string option values) — maps to "" in filter state */
const FILTER_ANY = "all";

const STATUS_FILTER_OPTIONS = [
  { value: FILTER_ANY, label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/** SP profile uses STATES_LIST `value`; NDIS price catalogue states use lowercase abbrev in `name` (e.g. nsw). */
const resolveSpProfileStateToCatalogStateId = (profileStateValue, catalogStates) => {
  if (profileStateValue == null || String(profileStateValue).trim() === "") return null;
  if (!Array.isArray(catalogStates) || catalogStates.length === 0) return null;

  const trimmed = String(profileStateValue).trim();
  const listEntry =
    STATES_LIST.find((item) => item.value === trimmed) ||
    STATES_LIST.find((item) => String(item.value).toLowerCase() === trimmed.toLowerCase()) ||
    STATES_LIST.find((item) => String(item.label).toLowerCase() === trimmed.toLowerCase());

  const abbrev = listEntry?.label?.toLowerCase();
  if (abbrev) {
    const row = catalogStates.find((s) => String(s.name).toLowerCase() === abbrev);
    if (row?.id != null) return String(row.id);
  }

  const byName = catalogStates.find((s) => String(s.name).toLowerCase() === trimmed.toLowerCase());
  if (byName?.id != null) return String(byName.id);

  return null;
};

const toNumericId = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const getLineItemServiceTypeId = (row) => {
  const invoiceServiceTypeId = toNumericId(row?.invoice_service_type_id ?? row?.invoice_service_type?.id);
  if (invoiceServiceTypeId !== null) return invoiceServiceTypeId;
  return toNumericId(row?.service_category_id ?? row?.service_category?.id);
};

const SERVICE_CATEGORY_IN_USE_DELETE_MSG =
  "This Service Category cannot be deleted because one or more associated Line Items are currently used in existing rosters.";

const canDeleteServiceCategory = (sc) => {
  if (!sc || sc.is_system) return false;
  if (sc.deleteCheckPending) return false;
  if (sc.has_line_items_in_use === true) return false;
  if (sc.can_delete === false) return false;
  return true;
};

const isServiceCategoryDeleteBlocked = (sc) =>
  Boolean(sc) && !sc.deleteCheckPending && !canDeleteServiceCategory(sc);

const isCurrentServiceType = (serviceType) => {
  const fundingType = serviceType?.funding_type || "NDIS";
  if (fundingType !== "NDIS") return true;
  if (!serviceType?.is_system) return true;
  return Boolean(serviceType?.is_ndis_category && serviceType?.ndis_budget_type_id);
};

/** Same rule as shift creation: match invoice_service_types.name to price_catalogs.support_category_name_pace */
const getCatalogueItemsForServiceType = (serviceType, paceCategories = []) => {
  const serviceTypeName = String(serviceType?.name || "").trim().toLowerCase();
  const paceGroup = (paceCategories || []).find(
    (paceCat) => String(paceCat?.pace_category_name || "").trim().toLowerCase() === serviceTypeName
  );
  return paceGroup?.items || [];
};

const parseSortableTimestamp = (value) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const newestFirstCompare = (a, b) => {
  const bCreatedAt = parseSortableTimestamp(b?.created_at);
  const aCreatedAt = parseSortableTimestamp(a?.created_at);
  if (bCreatedAt !== aCreatedAt) return bCreatedAt - aCreatedAt;

  const bId = Number(b?.id ?? 0);
  const aId = Number(a?.id ?? 0);
  if (!Number.isNaN(bId) && !Number.isNaN(aId) && bId !== aId) return bId - aId;

  return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
};

const CustomLineItems = () => {
  const { sideBarPadding } = useContext(LoginContext);
  const spSettings = useSelector(getSpSetting);
  
  // Get allowed funding types based on care sector
  const FUNDING_TYPES = useMemo(() => {
    return getAllowedFundingTypes(spSettings?.care_sector);
  }, [spSettings?.care_sector]);
  
  const FUNDING_FILTER_OPTIONS = useMemo(() => {
    return [
      { value: FILTER_ANY, label: "All" },
      ...FUNDING_TYPES.map(({ value, label }) => ({ value, label })),
    ];
  }, [FUNDING_TYPES]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [fundingFilter, setFundingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categoryFieldErrors, setCategoryFieldErrors] = useState({});
  const [categorySubmitError, setCategorySubmitError] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const categoryDeleteCheckRef = React.useRef(0);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [categoryFundingType, setCategoryFundingType] = useState("NDIS");
  const [categoryBudgetTypeId, setCategoryBudgetTypeId] = useState(null);
  const [expandedFundingTypes, setExpandedFundingTypes] = useState(() => new Set());
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [systemCatalogueByCategory, setSystemCatalogueByCategory] = useState([]);
  const [ndisBudgetTypes, setNdisBudgetTypes] = useState([]);
  const [expandedBudgetTypes, setExpandedBudgetTypes] = useState(() => new Set());
  const [hierarchyLoading, setHierarchyLoading] = useState(true);
  const hierarchyRefreshPromiseRef = React.useRef(null);
  const pendingLineItemPresetRef = React.useRef(null);

  const serviceTypeIdsMatch = (a, b) =>
    a != null && b != null && String(a) !== "" && String(a) === String(b);

  const upsertServiceCategory = useCallback((category) => {
    if (!category?.id) return;
    setServiceCategories((prev) => {
      const idx = prev.findIndex((sc) => sc.id === category.id);
      if (idx === -1) return [...prev, category];
      const next = [...prev];
      next[idx] = { ...next[idx], ...category };
      return next;
    });
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      description: "",
      funding_type: "NDIS",
      unique_code: "",
      rate: "",
      rate_type: "single",
      state_id: "",
      invoice_service_type_id: "",
      rate_weekday_daytime: "",
      rate_weekday_evening: "",
      rate_weekday_night: "",
      rate_saturday: "",
      rate_sunday: "",
      rate_public_holiday: "",
      rate_sleepover: "",
      multiplier_weekend: "",
      multiplier_public_holiday: "",
      multiplier_overtime_1_5: "",
      multiplier_overtime_2: "",
    },
  });

  const watchFundingType = watch("funding_type");
  const watchRate = watch("rate");
  const watchServiceCategoryId = watch("invoice_service_type_id");

  const isNdisOrganisation = spSettings?.care_sector !== "aged_care";

  const spDefaultNdisStateId = useMemo(() => {
    if (!isNdisOrganisation) return null;
    return resolveSpProfileStateToCatalogStateId(spSettings?.state, states);
  }, [isNdisOrganisation, spSettings?.state, states]);

  const fetchList = (filters = {}) => {
    setLoading(true);
    const params = new URLSearchParams();
    const effectiveSearch = filters.search ?? appliedSearch;
    const effectiveFundingFilter = filters.fundingFilter ?? fundingFilter;
    const effectiveStatusFilter = filters.statusFilter ?? statusFilter;
    const effectiveCategoryFilter = filters.categoryFilter ?? categoryFilter;
    if (effectiveSearch) params.set("search", effectiveSearch);
    if (effectiveFundingFilter) params.set("funding_type", effectiveFundingFilter);
    if (effectiveStatusFilter) params.set("status", effectiveStatusFilter);
    if (effectiveCategoryFilter) params.set("invoice_service_type_id", effectiveCategoryFilter);
    const query = params.toString();
    apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}${query ? `?${query}` : ""}`)
      .then((res) => {
        setItems(res?.data?.data ?? []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  const fetchServiceCategories = (fundingType = null) => {
    // Load categories from the dedicated custom-line-items endpoint which:
    // - Returns is_system correctly (sp_id === null means system/read-only)
    // - Returns ALL funding types (NDIS, PRIVATE, OTHER, AGED_CARE)
    const params = new URLSearchParams();
    if (fundingType) params.set('funding_type', fundingType);
    const url = `${URL_CUSTOM_LINE_ITEMS}/service-categories${params.toString() ? '?' + params.toString() : ''}`;
    return apiGetRequest(url)
      .then((res) => {
        setServiceCategories(res?.data?.data ?? []);
      })
      .catch(() => setServiceCategories([]));
  };

  const fetchNdisBudgetTypes = () => {
    return apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}/ndis-budget-types`)
      .then((res) => setNdisBudgetTypes(res?.data?.data ?? []))
      .catch(() => setNdisBudgetTypes([]));
  };

  const fetchSystemCatalogueItems = () => {
    return apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}/system-catalogue-items`)
      .then((res) => setSystemCatalogueByCategory(res?.data?.data ?? []))
      .catch(() => setSystemCatalogueByCategory([]));
  };

  const fetchStates = () => {
    return apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}/states`)
      .then((res) => setStates(res?.data?.data ?? []))
      .catch(() => setStates([]));
  };

  const refreshHierarchyData = () => {
    setHierarchyLoading(true);
    const promise = Promise.allSettled([
      fetchServiceCategories(),
      fetchNdisBudgetTypes(),
      fetchSystemCatalogueItems(),
    ]).finally(() => {
      setHierarchyLoading(false);
      if (hierarchyRefreshPromiseRef.current === promise) {
        hierarchyRefreshPromiseRef.current = null;
      }
    });
    hierarchyRefreshPromiseRef.current = promise;
    return promise;
  };

  useEffect(() => {
    fetchList();
  }, [statusFilter, fundingFilter, categoryFilter, appliedSearch]);

  useEffect(() => {
    refreshHierarchyData();
    fetchStates();
  }, []);

  // Drop filters that belong to the other care sector after NDIS ↔ Aged Care switch
  useEffect(() => {
    const allowed = new Set(FUNDING_TYPES.map((ft) => ft.value));
    if (fundingFilter && !allowed.has(fundingFilter)) {
      setFundingFilter("");
    }
    if (!categoryFilter || serviceCategories.length === 0) return;
    const cat = serviceCategories.find((sc) => String(sc.id) === String(categoryFilter));
    if (!cat || !allowed.has(cat.funding_type || "NDIS")) {
      setCategoryFilter("");
    }
  }, [FUNDING_TYPES, fundingFilter, categoryFilter, serviceCategories]);

  // When funding type changes in line item form, clear category so user picks from the correct funding-type list
  const prevFundingTypeRef = React.useRef(watchFundingType);
  useEffect(() => {
    if (!modalOpen || editingItem) {
      if (!modalOpen) {
        prevFundingTypeRef.current = watchFundingType;
        pendingLineItemPresetRef.current = null;
      }
      return;
    }

    // Only clear when the user changes funding type inside the modal — not on preset open
    if (prevFundingTypeRef.current !== watchFundingType) {
      pendingLineItemPresetRef.current = null;
      setValue("invoice_service_type_id", "");
      if (watchFundingType === "NDIS" && spDefaultNdisStateId) {
        setValue("state_id", spDefaultNdisStateId);
      } else if (watchFundingType !== "NDIS") {
        setValue("state_id", "");
      }
      prevFundingTypeRef.current = watchFundingType;
    }
  }, [watchFundingType, modalOpen, editingItem, spDefaultNdisStateId, setValue]);

  // New NDIS line item: apply SP profile state once modal is open / states list loads (catalogue state ids).
  useEffect(() => {
    if (!modalOpen || editingItem) return;
    if (watchFundingType !== "NDIS" || !spDefaultNdisStateId) return;
    setValue("state_id", spDefaultNdisStateId);
  }, [modalOpen, editingItem, watchFundingType, spDefaultNdisStateId, setValue]);

  const handleSearch = () => {
    setAppliedSearch(String(search || "").trim());
  };

  const clearAllFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setFundingFilter("");
    setStatusFilter("");
    setCategoryFilter("");
    fetchList({
      search: "",
      fundingFilter: "",
      statusFilter: "",
      categoryFilter: "",
    });
  };

  const hasActiveFilters = Boolean(
    appliedSearch || fundingFilter || statusFilter || categoryFilter
  );

  const hasPendingSearch = String(search || "").trim() !== appliedSearch;

  const handleFilterKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleSearch();
  };

  const openCreate = async (presetFundingType = null, presetCategoryId = null) => {
    if (hierarchyRefreshPromiseRef.current) {
      await hierarchyRefreshPromiseRef.current;
    }
    const fundingType = presetFundingType || "NDIS";
    const categoryId = presetCategoryId != null ? String(presetCategoryId) : null;
    prevFundingTypeRef.current = fundingType;
    pendingLineItemPresetRef.current = categoryId ? { fundingType, categoryId } : null;
    setEditingItem(null);
    setSubmitError(null);
    setFieldErrors({});
    reset({
      description: "",
      funding_type: fundingType,
      rate: "",
      unit: "",
      rate_type: "single",
      state_id: "",
      invoice_service_type_id: categoryId ?? "",
      rate_weekday_daytime: "",
      rate_weekday_evening: "",
      rate_weekday_night: "",
      rate_saturday: "",
      rate_sunday: "",
      rate_public_holiday: "",
      rate_sleepover: "",
      multiplier_weekend: "",
      multiplier_public_holiday: "",
      multiplier_overtime_1_5: "",
      multiplier_overtime_2: "",
      unique_code: "",
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    const fundingType = row.funding_type ?? "NDIS";
    prevFundingTypeRef.current = fundingType;
    pendingLineItemPresetRef.current = null;
    setEditingItem(row);
    setSubmitError(null);
    setFieldErrors({});
    reset({
      description: row.description ?? "",
      funding_type: fundingType,
      rate: row.rate != null ? String(row.rate) : "",
      unit: row.unit ?? "",
      rate_type: row.rate_type ?? "single",
      state_id: row.state_id != null ? String(row.state_id) : "",
      invoice_service_type_id: row.invoice_service_type_id != null ? String(row.invoice_service_type_id) : "",
      rate_weekday_daytime: row.rate_weekday_daytime != null ? String(row.rate_weekday_daytime) : "",
      rate_weekday_evening: row.rate_weekday_evening != null ? String(row.rate_weekday_evening) : "",
      rate_weekday_night: row.rate_weekday_night != null ? String(row.rate_weekday_night) : "",
      rate_saturday: row.rate_saturday != null ? String(row.rate_saturday) : "",
      rate_sunday: row.rate_sunday != null ? String(row.rate_sunday) : "",
      rate_public_holiday: row.rate_public_holiday != null ? String(row.rate_public_holiday) : "",
      rate_sleepover: row.rate_sleepover != null ? String(row.rate_sleepover) : "",
      multiplier_weekend: row.multiplier_weekend != null ? String(row.multiplier_weekend) : "",
      multiplier_public_holiday: row.multiplier_public_holiday != null ? String(row.multiplier_public_holiday) : "",
      multiplier_overtime_1_5: row.multiplier_overtime_1_5 != null ? String(row.multiplier_overtime_1_5) : "",
      multiplier_overtime_2: row.multiplier_overtime_2 != null ? String(row.multiplier_overtime_2) : "",
      unique_code: "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    pendingLineItemPresetRef.current = null;
    setModalOpen(false);
    setEditingItem(null);
    setSubmitError(null);
    setFieldErrors({});
  };

  const onSubmitForm = (data) => {
    console.log("=== Custom Line Item Form Submission ===");
    console.log("Form Data:", data);
    
    setSubmitError(null);
    setFieldErrors({});
    
    // Service category is required for all funding types
    if (!data.invoice_service_type_id || String(data.invoice_service_type_id).trim() === "") {
      console.error("Validation failed: Service category is required");
      setFieldErrors({ invoice_service_type_id: ["Service category is required for all line items."] });
      setSubmitError("Please fix the errors below.");
      return;
    }

    if (!editingItem) {
      const uc = data.unique_code != null ? String(data.unique_code).trim() : "";
      if (!uc) {
        setFieldErrors({ unique_code: ["Code is required."] });
        setSubmitError("Please fix the errors below.");
        setError("unique_code", { type: "manual", message: "Code is required." });
        return;
      }
      if (uc.length > 50) {
        setFieldErrors({ unique_code: ["Code must be at most 50 characters."] });
        setSubmitError("Please fix the errors below.");
        setError("unique_code", { type: "manual", message: "Code must be at most 50 characters." });
        return;
      }
    }

    // Validate main rate (required, non-negative)
    const rateStr = data.rate != null ? String(data.rate).trim() : "";
    if (rateStr === "") {
      console.error("Validation failed: Rate is required");
      setFieldErrors({ rate: ["Rate is required."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate is required." });
      return;
    }
    const rateNum = parseFloat(rateStr);
    if (Number.isNaN(rateNum)) {
      console.error("Validation failed: Rate is not a number", rateStr);
      setFieldErrors({ rate: ["Rate must be a valid number."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate must be a valid number." });
      return;
    }
    if (rateNum < 0) {
      console.error("Validation failed: Rate is negative", rateNum);
      setFieldErrors({ rate: ["Rate must be 0 or greater."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate must be 0 or greater." });
      return;
    }
    const rateWholePart = rateStr.includes(".") ? rateStr.split(".")[0] : rateStr;
    const rateDecimalPart = rateStr.includes(".") ? rateStr.split(".")[1] : "";
    if (rateWholePart.length > 8) {
      setFieldErrors({ rate: ["Rate can have at most 8 digits before the decimal."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate can have at most 8 digits before the decimal." });
      return;
    }
    if (rateDecimalPart.length > 2) {
      setFieldErrors({ rate: ["Rate can have at most 2 decimal places."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate can have at most 2 decimal places." });
      return;
    }
    if (rateNum > 99999999.99) {
      setFieldErrors({ rate: ["Rate must not exceed 99,999,999.99."] });
      setSubmitError("Please fix the errors below.");
      setError("rate", { type: "manual", message: "Rate must not exceed 99,999,999.99." });
      return;
    }

    console.log("Basic validation passed");
    
    // Optional NDIS scenario rates: if any filled, must be valid non-negative number
    if (data.rate_type === "ndis_style") {
      const ndisFields = [
        "rate_weekday_daytime", "rate_weekday_evening", "rate_weekday_night",
        "rate_saturday", "rate_sunday", "rate_public_holiday", "rate_sleepover"
      ];
      for (const field of ndisFields) {
        const v = data[field];
        if (v !== "" && v != null && String(v).trim() !== "") {
          const n = parseFloat(v);
          if (Number.isNaN(n) || n < 0) {
            console.error(`Validation failed: ${field} is invalid`, v);
            setFieldErrors({ [field]: ["Must be a valid number ≥ 0, or leave blank to use main rate."] });
            setSubmitError("Please fix the errors below.");
            return;
          }
        }
      }
    }
    // Optional SCHADS multipliers: if any filled, must be valid non-negative number
    if (data.rate_type === "schads_style") {
      const schadsFields = ["multiplier_weekend", "multiplier_public_holiday", "multiplier_overtime_1_5", "multiplier_overtime_2"];
      for (const field of schadsFields) {
        const v = data[field];
        if (v !== "" && v != null && String(v).trim() !== "") {
          const n = parseFloat(v);
          if (Number.isNaN(n) || n < 0) {
            console.error(`Validation failed: ${field} is invalid`, v);
            setFieldErrors({ [field]: ["Must be a valid number ≥ 0, or leave blank for 1×."] });
            setSubmitError("Please fix the errors below.");
            return;
          }
        }
      }
    }

    setSubmitting(true);
    const fundingType = data.funding_type ?? editingItem?.funding_type ?? "NDIS";
    // Preserve unit/state on edit (fields omitted from edit UI to match create form).
    const payload = {
      description: (data.description || "").trim(),
      funding_type: fundingType,
      rate: Math.round(rateNum * 100) / 100,
      unit: editingItem
        ? (editingItem.unit ? String(editingItem.unit).trim() : null)
        : (data.unit ? String(data.unit).trim() : null),
      rate_type: data.rate_type || "single",
      state_id: editingItem
        ? (editingItem.state_id != null ? parseInt(editingItem.state_id, 10) : null)
        : (data.state_id ? parseInt(data.state_id, 10) : null),
      invoice_service_type_id: data.invoice_service_type_id ? parseInt(data.invoice_service_type_id, 10) : null,
    };

    if (!editingItem) {
      payload.unique_code = String(data.unique_code || "").trim();
    }

    console.log("Payload prepared:", payload);
    if (payload.rate_type === "ndis_style") {
      payload.rate_weekday_daytime = data.rate_weekday_daytime !== "" ? parseFloat(data.rate_weekday_daytime) : null;
      payload.rate_weekday_evening = data.rate_weekday_evening !== "" ? parseFloat(data.rate_weekday_evening) : null;
      payload.rate_weekday_night = data.rate_weekday_night !== "" ? parseFloat(data.rate_weekday_night) : null;
      payload.rate_saturday = data.rate_saturday !== "" ? parseFloat(data.rate_saturday) : null;
      payload.rate_sunday = data.rate_sunday !== "" ? parseFloat(data.rate_sunday) : null;
      payload.rate_public_holiday = data.rate_public_holiday !== "" ? parseFloat(data.rate_public_holiday) : null;
      payload.rate_sleepover = data.rate_sleepover !== "" ? parseFloat(data.rate_sleepover) : null;
    }
    if (payload.rate_type === "schads_style") {
      payload.multiplier_weekend = data.multiplier_weekend !== "" ? parseFloat(data.multiplier_weekend) : null;
      payload.multiplier_public_holiday = data.multiplier_public_holiday !== "" ? parseFloat(data.multiplier_public_holiday) : null;
      payload.multiplier_overtime_1_5 = data.multiplier_overtime_1_5 !== "" ? parseFloat(data.multiplier_overtime_1_5) : null;
      payload.multiplier_overtime_2 = data.multiplier_overtime_2 !== "" ? parseFloat(data.multiplier_overtime_2) : null;
    }
    if (editingItem) {
      payload.unique_code = editingItem.unique_code ?? "";
    }

    const request = editingItem
      ? apiPutRequest(`${URL_CUSTOM_LINE_ITEMS}/${editingItem.uuid}`, payload)
      : apiPostRequest(URL_CUSTOM_LINE_ITEMS, payload);

    request
      .then(() => {
        console.log("✅ Item saved successfully");
        toast.success(editingItem ? "Item updated" : "Item created");
        closeModal();
        fetchList();
      })
      .catch((e) => {
        console.error("❌ API Error:", e);
        console.error("Response:", e?.response);
        console.error("Status:", e?.response?.status);
        console.error("Data:", e?.response?.data);
        
        const status = e?.response?.status;
        const resData = e?.response?.data;
        if (status === 422) {
          console.error("Validation errors from server:", resData?.errors);
          const raw = resData?.errors || {};
          const errs = Object.fromEntries(
            Object.entries(raw).map(([k, v]) => [k, Array.isArray(v) ? v : [v == null ? "Invalid value" : String(v)]])
          );
          setFieldErrors(errs);
          setSubmitError(resData?.message || "Please fix the errors below.");
        } else if (status === 409) {
          toast.error(resData?.message || "This code is already in use or the item cannot be updated.");
        } else {
          toast.error(resData?.message || e?.message || (editingItem ? "Failed to update" : "Failed to create"));
        }
      })
      .finally(() => setSubmitting(false));
  };

  const isRowActionLoading = (row) =>
    actionLoading != null && String(actionLoading) === String(row?.uuid);

  const patchItemStatusLocally = (uuid, nextStatus) => {
    setItems((currentItems) => {
      if (statusFilter && statusFilter !== nextStatus) {
        return currentItems.filter((item) => String(item.uuid) !== String(uuid));
      }
      return currentItems.map((item) =>
        String(item.uuid) === String(uuid) ? { ...item, status: nextStatus } : item
      );
    });
  };

  const handleActivate = (uuid) => {
    if (!uuid || actionLoading) return;
    setActionLoading(uuid);
    apiPatchRequest(`${URL_CUSTOM_LINE_ITEMS}/${uuid}/activate`, {})
      .then(() => {
        patchItemStatusLocally(uuid, "active");
        toast.success("Item activated");
      })
      .catch((e) => toast.error(e?.response?.data?.message || "Failed to activate"))
      .finally(() => setActionLoading(null));
  };

  const handleDeactivate = (uuid) => {
    if (!uuid || actionLoading) return;
    setActionLoading(uuid);
    apiPatchRequest(`${URL_CUSTOM_LINE_ITEMS}/${uuid}/deactivate`, {})
      .then(() => {
        patchItemStatusLocally(uuid, "inactive");
        toast.success("Item deactivated");
      })
      .catch((e) => toast.error(e?.response?.data?.message || "Failed to deactivate"))
      .finally(() => setActionLoading(null));
  };

  const renderLineItemStatus = (row) => {
    if (isRowActionLoading(row)) {
      return (
        <span role='status' aria-label='Updating status' className='inline-flex h-6 w-6 items-center justify-center'>
          <span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600' aria-hidden />
        </span>
      );
    }
    return (
      <span
        className={`inline-flex px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded ring-1 ring-inset ${
          row.status === "active"
            ? "bg-emerald-50 text-emerald-900 ring-emerald-200/60"
            : "bg-slate-100 text-slate-700 ring-slate-200"
        }`}
      >
        {row.status}
      </span>
    );
  };

  const renderLineItemStatusToggle = (row, size = "sm") => {
    const textClass =
      size === "lg"
        ? "text-[11px] font-semibold rounded-lg px-2 py-1.5 transition-colors duration-150"
        : "text-[10px] font-semibold rounded px-1.5 py-1";
    const loading = isRowActionLoading(row);
    if (row.status === "inactive") {
      return (
        <button
          type='button'
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            handleActivate(row.uuid);
          }}
          className={`${textClass} text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-50`}
        >
          Activate
        </button>
      );
    }
    return (
      <button
        type='button'
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
          handleDeactivate(row.uuid);
        }}
        className={`${textClass} text-amber-700 hover:text-amber-800 hover:bg-amber-50 disabled:opacity-50`}
      >
        Deactivate
      </button>
    );
  };

  const handleDelete = (uuid, uniqueCode) => {
    setItemToDelete({ uuid, uniqueCode });
  };

  const closeDeleteModal = () => {
    if (!deleting) setItemToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (!itemToDelete) return;
    setDeleting(true);
    apiDeleteRequest(`${URL_CUSTOM_LINE_ITEMS}/${itemToDelete.uuid}`)
      .then(() => {
        toast.success("Item deleted");
        setItemToDelete(null);
        fetchList();
        fetchServiceCategories();
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || "Cannot delete: item may be in use.";
        toast.error(msg);
      })
      .finally(() => setDeleting(false));
  };

  const openAddCategory = (presetFundingType = null, presetBudgetTypeId = null) => {
    setEditingCategory(null);
    setCategoryName("");
    setCategoryFundingType(presetFundingType || "NDIS");
    setCategoryBudgetTypeId(presetBudgetTypeId || null);
    setCategoryFieldErrors({});
    setCategorySubmitError(null);
    setCategoryModalOpen(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryName(cat.name || "");
    setCategoryFundingType(cat.funding_type || "NDIS");
    setCategoryBudgetTypeId(cat.ndis_budget_type_id ?? null);
    setCategoryFieldErrors({});
    setCategorySubmitError(null);
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryName("");
    setCategoryFieldErrors({});
    setCategorySubmitError(null);
    setCategoryBudgetTypeId(null);
    setCategoryFundingType("NDIS");
  };

  const onSubmitCategory = () => {
    const name = (categoryName || "").trim();
    const fieldErrors = {};
    const nameError = getServiceCategoryNameValidationError(categoryName);
    if (nameError) {
      fieldErrors.name = nameError;
    }
    if (!editingCategory && !categoryFundingType) {
      fieldErrors.funding_type = "Funding type is required.";
    }
    if (!editingCategory && categoryFundingType === "NDIS" && !categoryBudgetTypeId) {
      fieldErrors.ndis_budget_type_id = "Budget type is required for NDIS categories.";
    }
    if (Object.keys(fieldErrors).length > 0) {
      setCategoryFieldErrors(fieldErrors);
      setCategorySubmitError("Please fix the errors below.");
      return;
    }
    setCategoryFieldErrors({});
    setCategorySubmitError(null);
    setCategorySubmitting(true);
    if (editingCategory) {
      const payload = { name };
      if ((editingCategory.funding_type || "NDIS") === "NDIS") {
        payload.ndis_budget_type_id = categoryBudgetTypeId || null;
      }
      apiPutRequest(`${URL_CUSTOM_LINE_ITEMS}/service-categories/${editingCategory.uuid ?? editingCategory.id}`, payload)
        .then((res) => {
          upsertServiceCategory(res?.data?.data);
          toast.success("Category updated");
          closeCategoryModal();
          refreshHierarchyData();
          fetchList();
        })
        .catch((e) => {
          const errs = e?.response?.data?.errors;
          const nextFieldErrors = {};
          if (errs?.name) {
            nextFieldErrors.name = Array.isArray(errs.name) ? errs.name[0] : errs.name;
          }
          if (errs?.ndis_budget_type_id) {
            nextFieldErrors.ndis_budget_type_id = Array.isArray(errs.ndis_budget_type_id)
              ? errs.ndis_budget_type_id[0]
              : errs.ndis_budget_type_id;
          }
          if (Object.keys(nextFieldErrors).length > 0) {
            setCategoryFieldErrors(nextFieldErrors);
            setCategorySubmitError("Please fix the errors below.");
          } else {
            setCategorySubmitError(e?.response?.data?.message || "Failed to update category.");
          }
        })
        .finally(() => setCategorySubmitting(false));
    } else {
      const payload = { name, funding_type: categoryFundingType };
      if (categoryFundingType === "NDIS" && categoryBudgetTypeId) {
        payload.ndis_budget_type_id = categoryBudgetTypeId;
      }
      apiPostRequest(`${URL_CUSTOM_LINE_ITEMS}/service-categories`, payload)
        .then((res) => {
          upsertServiceCategory(res?.data?.data);
          toast.success("Category created");
          closeCategoryModal();
          refreshHierarchyData();
        })
        .catch((e) => {
          const errs = e?.response?.data?.errors;
          const nextFieldErrors = {};
          if (errs?.name) {
            nextFieldErrors.name = Array.isArray(errs.name) ? errs.name[0] : errs.name;
          }
          if (errs?.funding_type) {
            nextFieldErrors.funding_type = Array.isArray(errs.funding_type) ? errs.funding_type[0] : errs.funding_type;
          }
          if (errs?.ndis_budget_type_id) {
            nextFieldErrors.ndis_budget_type_id = Array.isArray(errs.ndis_budget_type_id)
              ? errs.ndis_budget_type_id[0]
              : errs.ndis_budget_type_id;
          }
          if (Object.keys(nextFieldErrors).length > 0) {
            setCategoryFieldErrors(nextFieldErrors);
            setCategorySubmitError("Please fix the errors below.");
          } else {
            setCategorySubmitError(e?.response?.data?.message || "Failed to create category.");
          }
        })
        .finally(() => setCategorySubmitting(false));
    }
  };

  const openDeleteCategory = (sc) => {
    if (!sc || sc.is_system) return;
    const requestId = ++categoryDeleteCheckRef.current;
    setCategoryToDelete({
      ...sc,
      deleteCheckPending: true,
    });
    apiGetRequest(`${URL_CUSTOM_LINE_ITEMS}/service-categories/${sc.uuid ?? sc.id}/delete-eligibility`)
      .then((res) => {
        if (categoryDeleteCheckRef.current !== requestId) return;
        const data = res?.data?.data ?? {};
        const hasLineItemsInUse = data.has_line_items_in_use === true;
        setCategoryToDelete((prev) => {
          if (!prev || String(prev.id) !== String(sc.id)) return prev;
          return {
            ...prev,
            has_line_items_in_use: hasLineItemsInUse,
            can_delete: data.can_delete === false ? false : !hasLineItemsInUse,
            deleteCheckPending: false,
          };
        });
      })
      .catch(() => {
        if (categoryDeleteCheckRef.current !== requestId) return;
        // DELETE still rejects an in-use category if this pre-check cannot be loaded.
        setCategoryToDelete((prev) => {
          if (!prev || String(prev.id) !== String(sc.id)) return prev;
          return {
            ...prev,
            has_line_items_in_use: false,
            can_delete: true,
            deleteCheckPending: false,
          };
        });
      });
  };

  const closeDeleteCategoryModal = () => {
    if (!deletingCategory) {
      categoryDeleteCheckRef.current += 1;
      setCategoryToDelete(null);
    }
  };

  const handleDeleteCategoryConfirm = () => {
    if (!categoryToDelete || !canDeleteServiceCategory(categoryToDelete)) return;
    setDeletingCategory(true);
    // invoice_service_types has no uuid column; fall back to id
    apiDeleteRequest(`${URL_CUSTOM_LINE_ITEMS}/service-categories/${categoryToDelete.uuid ?? categoryToDelete.id}`)
      .then(() => {
        toast.success("Category deleted");
        setCategoryToDelete(null);
        refreshHierarchyData();
        fetchList();
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Cannot delete category.");
      })
      .finally(() => setDeletingCategory(false));
  };

  const fundingTypeLabel = (ft) => {
    const o = FUNDING_TYPES.find((x) => x.value === ft);
    return o ? o.label : ft || "—";
  };
  // Subtle badge tint per funding type; section headers stay neutral for a calmer layout
  const fundingTypeRowColors = (value) => {
    switch (value) {
      case "NDIS":
        return { row: "bg-white hover:bg-slate-50/90", badge: "bg-indigo-50/90 text-indigo-800 ring-1 ring-inset ring-indigo-100" };
      case "AGED_CARE":
        return { row: "bg-white hover:bg-slate-50/90", badge: "bg-emerald-50/90 text-emerald-900 ring-1 ring-inset ring-emerald-100/80" };
      case "PRIVATE":
        return { row: "bg-white hover:bg-slate-50/90", badge: "bg-amber-50/90 text-amber-900 ring-1 ring-inset ring-amber-100/80" };
      case "OTHER":
        return { row: "bg-white hover:bg-slate-50/90", badge: "bg-slate-100 text-slate-800 ring-1 ring-inset ring-slate-200/80" };
      default:
        return { row: "bg-white hover:bg-slate-50/90", badge: "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200/80" };
    }
  };
  const toggleFundingType = (value) => {
    setExpandedFundingTypes((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };
  const toggleCategory = (key) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleBudgetType = (key) => {
    setExpandedBudgetTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const categoryKey = (sc) => `cat-${sc.id}`;
  const budgetTypeKey = (bt) => `bt-${bt.id}`;
  const getSystemCatalogueDataForCategory = (categoryId) => {
    const group = systemCatalogueByCategory.find((c) => c.service_category_id === categoryId);
    return group ?? null;
  };
  const visibleServiceTypes = useMemo(() => {
    // Scope to care sector: NDIS orgs → NDIS/Aged Care/Private/Other; Aged Care orgs → Aged Care/Private/Other.
    // Without this, the Service type filter still lists the other sector after switching care_sector.
    const allowedFundingValues = new Set(FUNDING_TYPES.map((ft) => ft.value));
    const currentServiceTypes = serviceCategories.filter((sc) => {
      if (!isCurrentServiceType(sc)) return false;
      return allowedFundingValues.has(sc.funding_type || "NDIS");
    });
    return [...currentServiceTypes].sort((a, b) => {
      const aIsSystem = Boolean(a?.is_system);
      const bIsSystem = Boolean(b?.is_system);
      if (aIsSystem !== bIsSystem) {
        return aIsSystem ? 1 : -1;
      }
      return newestFirstCompare(a, b);
    });
  }, [serviceCategories, FUNDING_TYPES]);

  // Group invoice service types by funding type. These are called "categories" in
  // legacy endpoints, but invoice_service_types is the real grouping table here.
  const categoriesByFundingType = FUNDING_TYPES.reduce((acc, { value }) => {
    acc[value] = visibleServiceTypes.filter((sc) => (sc.funding_type || "NDIS") === value);
    return acc;
  }, {});

  // Group custom line items by invoice_service_type_id only. service_category_id is
  // kept as a backend compatibility alias, so using both can double-count/misplace rows.
  const itemsByCategoryId = {};
  visibleServiceTypes.forEach((sc) => {
    itemsByCategoryId[sc.id] = items
      .filter((row) => getLineItemServiceTypeId(row) === toNumericId(sc.id))
      .sort(newestFirstCompare);
  });

  // Which funding type sections to show: respect funding filter and category filter
  const isListFiltered = Boolean(appliedSearch || statusFilter || categoryFilter);

  const appliedSearchTerm = appliedSearch.trim().toLowerCase();

  const catalogueItemMatchesSearch = useCallback(
    (item) => {
      if (!appliedSearchTerm) return true;
      return (
        String(item?.support_item_number || "")
          .toLowerCase()
          .includes(appliedSearchTerm) ||
        String(item?.support_item_name || "")
          .toLowerCase()
          .includes(appliedSearchTerm)
      );
    },
    [appliedSearchTerm]
  );

  const filterCatalogueItems = useCallback(
    (items = []) => {
      if (!appliedSearchTerm) return items;
      return (items || []).filter(catalogueItemMatchesSearch);
    },
    [appliedSearchTerm, catalogueItemMatchesSearch]
  );

  const filterPaceCategories = useCallback(
    (paceCategories = []) => {
      if (!appliedSearchTerm) return paceCategories;
      return (paceCategories || [])
        .map((paceCat) => {
          const nameMatches = String(paceCat?.pace_category_name || "")
            .toLowerCase()
            .includes(appliedSearchTerm);
          return {
            ...paceCat,
            items: nameMatches
              ? paceCat.items || []
              : (paceCat.items || []).filter(catalogueItemMatchesSearch),
          };
        })
        .filter(
          (paceCat) =>
            (paceCat.items || []).length > 0 ||
            String(paceCat?.pace_category_name || "")
              .toLowerCase()
              .includes(appliedSearchTerm)
        );
    },
    [appliedSearchTerm, catalogueItemMatchesSearch]
  );

  const visibleFundingTypes = (() => {
    if (fundingFilter) return FUNDING_TYPES.filter((f) => f.value === fundingFilter);
    if (categoryFilter) {
      const cat = visibleServiceTypes.find((sc) => String(sc.id) === String(categoryFilter));
      if (cat?.funding_type) return FUNDING_TYPES.filter((f) => f.value === (cat.funding_type || "NDIS"));
    }
    return FUNDING_TYPES;
  })();

  const serviceTypeMatchesAppliedSearch = useCallback(
    (sc) => {
      if (!appliedSearchTerm) return true;
      if (String(sc?.name || "")
        .toLowerCase()
        .includes(appliedSearchTerm)) {
        return true;
      }
      if ((itemsByCategoryId[sc.id] || []).length > 0) return true;
      if ((sc.funding_type || "NDIS") !== "NDIS" || !sc.ndis_budget_type_id) return false;
      const paceCats =
        getSystemCatalogueDataForCategory(sc.ndis_budget_type_id)?.pace_categories || [];
      return getCatalogueItemsForServiceType(sc, paceCats).some(catalogueItemMatchesSearch);
    },
    [appliedSearchTerm, itemsByCategoryId, catalogueItemMatchesSearch, systemCatalogueByCategory]
  );

  const catsToShowForFunding = (value) => {
    let cats = categoriesByFundingType[value] || [];
    if (categoryFilter) {
      return cats.filter(
        (sc) => String(sc.id) === String(categoryFilter) || sc.id === parseInt(categoryFilter, 10)
      );
    }
    if (appliedSearchTerm) {
      return cats.filter(serviceTypeMatchesAppliedSearch);
    }
    if (isListFiltered) {
      cats = cats.filter((sc) => (itemsByCategoryId[sc.id] || []).length > 0);
    }
    return cats;
  };

  const getOrphanPaceCategories = (catsInBt, paceCategories) => {
    if (!appliedSearchTerm) return [];
    const covered = new Set(
      (catsInBt || []).map((sc) => String(sc?.name || "").trim().toLowerCase())
    );
    return (paceCategories || []).filter((paceCat) => {
      const name = String(paceCat?.pace_category_name || "").trim().toLowerCase();
      if (!name || covered.has(name)) return false;
      return (
        (paceCat.items || []).length > 0 ||
        name.includes(appliedSearchTerm)
      );
    });
  };

  const ndisBudgetTypeHasVisibleContent = (bt, catsInBt, paceCategories) => {
    if (catsInBt.length > 0) return true;
    if (!appliedSearchTerm) return false;
    return filterPaceCategories(paceCategories).length > 0;
  };

  const fundingTypesToRender = useMemo(() => {
    if (!isListFiltered) return visibleFundingTypes;
    return visibleFundingTypes.filter(({ value }) => {
      const cats = catsToShowForFunding(value);
      if (cats.length > 0) return true;
      if (value !== "NDIS" || !appliedSearchTerm) return false;
      return (systemCatalogueByCategory || []).some((group) => {
        const paceCategories = group?.pace_categories || [];
        return filterPaceCategories(paceCategories).length > 0;
      });
    });
  }, [
    isListFiltered,
    visibleFundingTypes,
    appliedSearchTerm,
    items,
    categoryFilter,
    fundingFilter,
    filterPaceCategories,
    systemCatalogueByCategory,
  ]);

  const expandAllAccordions = () => {
    const typesToExpand = isListFiltered ? fundingTypesToRender : visibleFundingTypes;
    const fundingKeys = typesToExpand.map((f) => f.value);
    setExpandedFundingTypes(new Set(fundingKeys));
    const budgetKeys = new Set();
    const catKeys = new Set();
    fundingKeys.forEach((ft) => {
      if (ft === "NDIS") {
        ndisBudgetTypes.forEach((bt) => {
          const catsInBt = catsToShowForFunding(ft).filter((sc) => sc.ndis_budget_type_id === bt.id);
          const systemCatData = getSystemCatalogueDataForCategory(bt.id);
          const paceCategories = filterPaceCategories(systemCatData?.pace_categories || []);
          if (!ndisBudgetTypeHasVisibleContent(bt, catsInBt, paceCategories)) return;
          budgetKeys.add(budgetTypeKey(bt));
          catsInBt.forEach((sc) => catKeys.add(categoryKey(sc)));
          getOrphanPaceCategories(catsInBt, paceCategories).forEach((paceCat) => {
            catKeys.add(
              `pace-${bt.id}-${String(paceCat.pace_category_name || "").trim().toLowerCase()}`
            );
          });
        });
        catsToShowForFunding(ft)
          .filter((sc) => !sc.ndis_budget_type_id)
          .forEach((sc) => catKeys.add(categoryKey(sc)));
      } else {
        catsToShowForFunding(ft).forEach((sc) => catKeys.add(categoryKey(sc)));
      }
    });
    setExpandedBudgetTypes(budgetKeys);
    setExpandedCategories(catKeys);
  };

  const collapseAllAccordions = () => {
    setExpandedFundingTypes(new Set());
    setExpandedBudgetTypes(new Set());
    setExpandedCategories(new Set());
  };

  useEffect(() => {
    if (!hasActiveFilters) return;
    expandAllAccordions();
  }, [appliedSearch, statusFilter, fundingFilter, categoryFilter]);

  // Only categories for the selected funding type in the line item form
  const formCategoryOptions = useMemo(() => {
    return visibleServiceTypes
      .filter((sc) => (sc.funding_type || "NDIS") === watchFundingType)
      .map((sc) => ({
        value: String(sc.id),
        label: sc.name || `Service type ${sc.id}`,
      }));
  }, [visibleServiceTypes, watchFundingType]);

  // Re-apply service type preset after modal open (guards against effects that run on first open)
  useEffect(() => {
    if (!modalOpen || editingItem || !pendingLineItemPresetRef.current?.categoryId) return;
    const { categoryId } = pendingLineItemPresetRef.current;
    if (!serviceTypeIdsMatch(watchServiceCategoryId, categoryId)) {
      setValue("invoice_service_type_id", String(categoryId));
    }
  }, [modalOpen, editingItem, watchServiceCategoryId, formCategoryOptions, setValue]);

  const formFundingTypeSelectedOption = useMemo(
    () => FUNDING_TYPES.find((o) => o.value === watchFundingType) || FUNDING_TYPES[0],
    [FUNDING_TYPES, watchFundingType]
  );

  // Stable reference for default option
  const defaultCategoryOption = useMemo(() => ({ 
    value: "", 
    label: "Select service type (required)..." 
  }), []);

  // Memoize the selected category option to prevent flickering
  const formCategorySelectedOption = useMemo(() => {
    const categoryId = watchServiceCategoryId;
    if (!categoryId || String(categoryId).trim() === "") return defaultCategoryOption;
    const fromOptions = formCategoryOptions.find((o) => serviceTypeIdsMatch(o.value, categoryId));
    if (fromOptions) return fromOptions;
    const fromAll = visibleServiceTypes.find((sc) => serviceTypeIdsMatch(sc.id, categoryId));
    if (fromAll) {
      return {
        value: String(fromAll.id),
        label: fromAll.name || `Service type ${fromAll.id}`,
      };
    }
    return defaultCategoryOption;
  }, [watchServiceCategoryId, formCategoryOptions, visibleServiceTypes, defaultCategoryOption]);

  // Memoize the complete dropdown data to prevent array recreation
  const formCategoryDropdownData = useMemo(() => {
    return [defaultCategoryOption, ...formCategoryOptions];
  }, [defaultCategoryOption, formCategoryOptions]);

  const serviceTypesForFilter = useMemo(() => {
    if (!fundingFilter) return visibleServiceTypes;
    return visibleServiceTypes.filter((sc) => (sc.funding_type || "NDIS") === fundingFilter);
  }, [visibleServiceTypes, fundingFilter]);

  const categoryFilterSelectData = useMemo(
    () => [
      { value: FILTER_ANY, label: "All" },
      ...serviceTypesForFilter.map((sc) => ({
        value: String(sc.id),
        label: sc.name || `Service type ${sc.id}`,
      })),
    ],
    [serviceTypesForFilter]
  );

  const statusFilterSelected =
    STATUS_FILTER_OPTIONS.find((o) => (statusFilter === "" ? o.value === FILTER_ANY : o.value === statusFilter)) ||
    STATUS_FILTER_OPTIONS[0];

  const fundingFilterSelected =
    FUNDING_FILTER_OPTIONS.find((o) => (fundingFilter === "" ? o.value === FILTER_ANY : o.value === fundingFilter)) ||
    FUNDING_FILTER_OPTIONS[0];

  const categoryFilterSelected =
    categoryFilterSelectData.find((o) =>
      categoryFilter === "" ? o.value === FILTER_ANY : String(o.value) === String(categoryFilter)
    ) || categoryFilterSelectData[0];

  const onStatusFilterChange = (opt) => {
    const v = opt?.value;
    setStatusFilter(v == null || v === FILTER_ANY ? "" : String(v));
  };

  const onFundingFilterChange = (opt) => {
    const v = opt?.value;
    const nextFundingFilter = v == null || v === FILTER_ANY ? "" : String(v);
    setFundingFilter(nextFundingFilter);

    if (!categoryFilter) return;
    const selectedCategory = visibleServiceTypes.find((sc) => String(sc.id) === String(categoryFilter));
    if (!selectedCategory) {
      setCategoryFilter("");
      return;
    }
    if (nextFundingFilter && (selectedCategory.funding_type || "NDIS") !== nextFundingFilter) {
      setCategoryFilter("");
    }
  };

  const onCategoryFilterChange = (opt) => {
    const v = opt?.value;
    const nextCategoryFilter = v == null || v === FILTER_ANY ? "" : String(v);
    setCategoryFilter(nextCategoryFilter);

    if (!nextCategoryFilter || fundingFilter) return;
    const selectedCategory = visibleServiceTypes.find((sc) => String(sc.id) === String(nextCategoryFilter));
    if (selectedCategory?.funding_type) {
      setFundingFilter(selectedCategory.funding_type || "NDIS");
    }
  };

  const activeFilterSummary = useMemo(() => {
    const chips = [];
    if (appliedSearch) chips.push({ key: "search", label: `Search: "${appliedSearch}"` });
    if (statusFilter) {
      const statusLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter;
      chips.push({ key: "status", label: `Status: ${statusLabel}` });
    }
    if (fundingFilter) {
      const fundingLabel = FUNDING_FILTER_OPTIONS.find((o) => o.value === fundingFilter)?.label || fundingFilter;
      chips.push({ key: "funding", label: `Funding: ${fundingLabel}` });
    }
    if (categoryFilter) {
      const categoryLabel =
        categoryFilterSelectData.find((o) => String(o.value) === String(categoryFilter))?.label || categoryFilter;
      chips.push({ key: "category", label: `Service type: ${categoryLabel}` });
    }
    return chips;
  }, [appliedSearch, statusFilter, fundingFilter, categoryFilter, FUNDING_FILTER_OPTIONS, categoryFilterSelectData]);

  return (
    <>
      <SideBarNew classes='bg-gradient' />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col min-h-screen bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)]`}
      >
        <main className='flex-1'>
          <Header
            title='Service Categories & Line Items'
            breadcrumb={
              <Breadcrumb
                pages={SP_SETTING_BREADCRUMB(SETTINGS_BREADCRUMB_PAGES.CUSTOM_LINE_ITEMS)}
              />
            }
          />
          <section className='container mx-auto sm:px-6 lg:px-8 mt-3 pb-12'>
            <div className='rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-10'>
              <p className='text-sm text-slate-600 leading-relaxed max-w-2xl mb-6'>
                Manage service types and line items by funding type for rostering and invoicing. Service types are grouped by funding type; under each service type you can manage its custom line items.
              </p>
              
              {/* Info Banner */}
              <div className='mb-8 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-5'>
                <div className='flex gap-3'>
                  <span className='inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white mt-0.5'>
                    <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                      <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
                    </svg>
                  </span>
                  <div className='flex-1 min-w-0'>
                    <h3 className='text-sm font-semibold text-blue-900 mb-2'>How Line Items Work</h3>
                    <div className='space-y-2 text-xs text-blue-800 leading-relaxed'>
                      <p>
                        <strong className='font-semibold'>NDIS:</strong> Contains both <span className='px-1.5 py-0.5 bg-slate-100 rounded text-slate-700 font-medium'>read-only government price catalogue</span> (organized by sub-categories) and{' '}
                        <span className='px-1.5 py-0.5 bg-blue-100 rounded text-blue-700 font-medium'>custom line items you create</span>. Use "Add custom item" to create your own pricing.
                      </p>
                      <p>
                        <strong className='font-semibold'>Aged Care, Private, Other:</strong> Contains only custom line items you create. Use "Add line item" to create pricing for these funding types.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className='mb-8 pb-8 border-b border-gray-100'>
                <div className='rounded-xl border border-gray-200 bg-white p-5 sm:p-6'>
                  <div className='flex flex-col gap-4' onKeyDown={handleFilterKeyDown}>
                    <div className='flex flex-col xl:flex-row xl:flex-wrap xl:items-end gap-4'>
                      <div className='w-full xl:flex-1 xl:min-w-[220px] xl:max-w-sm'>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>Search</label>
                        <Search
                          search={search}
                          setSearch={setSearch}
                          placeholder='Search code or description'
                          width='w-full'
                          customClassSearch='w-full min-w-0 !py-2 !border-gray-300 !rounded-lg !shadow-none focus-within:!ring-1 focus-within:!ring-blue-500/30'
                        />
                      </div>
                      <div className='w-full sm:w-auto sm:min-w-[160px] xl:min-w-[170px]'>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>Status</label>
                        <SelectOption
                          data={STATUS_FILTER_OPTIONS}
                          selectedOption={statusFilterSelected}
                          handleChange={onStatusFilterChange}
                          newDesign
                          cssClass='normal-case'
                        />
                      </div>
                      <div className='w-full sm:w-auto sm:min-w-[160px] xl:min-w-[170px]'>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>Funding type</label>
                        <SelectOption
                          data={FUNDING_FILTER_OPTIONS}
                          selectedOption={fundingFilterSelected}
                          handleChange={onFundingFilterChange}
                          newDesign
                          cssClass='normal-case'
                        />
                      </div>
                      <div className='w-full sm:w-auto sm:min-w-[200px] xl:min-w-[220px] xl:flex-1 xl:max-w-xs'>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>Service type</label>
                        <SelectOption
                          data={categoryFilterSelectData}
                          selectedOption={categoryFilterSelected}
                          handleChange={onCategoryFilterChange}
                          isSearch
                          newDesign
                          cssClass='normal-case'
                        />
                      </div>
                      <div className='flex items-center gap-2 shrink-0 xl:ml-auto pt-1 xl:pt-0'>
                        <CustomButton
                          Icon={<BsFilterCircle />}
                          label='Search'
                          clickHandler={handleSearch}
                          showLoading={loading}
                          isDisabled={loading}
                          cssClass='!normal-case whitespace-nowrap shrink-0'
                        />
                        <CustomButton
                          variant='secondary'
                          Icon={<FiDelete />}
                          label='Clear filters'
                          clickHandler={clearAllFilters}
                          isDisabled={!hasActiveFilters && !hasPendingSearch}
                          cssClass='!normal-case whitespace-nowrap shrink-0'
                        />
                      </div>
                    </div>
                    {(hasActiveFilters || hasPendingSearch) && (
                      <div className='flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100'>
                        {hasPendingSearch && (
                          <span className='text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1'>
                            Text search not applied yet — press Search or Enter
                          </span>
                        )}
                        {activeFilterSummary.map((chip) => (
                          <span
                            key={chip.key}
                            className='inline-flex items-center text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1'
                          >
                            {chip.label}
                          </span>
                        ))}
                        {hasActiveFilters && (
                          <span className='text-xs text-slate-500'>
                            Matching sections are expanded below.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            {loading || hierarchyLoading ? (
              <div className='flex justify-center py-16'>
                <CircularLoader />
              </div>
            ) : (
              <div className='flex flex-col gap-0'>
                <div className='flex justify-end items-center gap-0 pb-3 mb-4 border-b border-gray-200'>
                  <button
                    type='button'
                    onClick={expandAllAccordions}
                    className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 rounded-sm'
                  >
                    Expand all
                  </button>
                  <span className='mx-3 text-gray-300 select-none' aria-hidden>
                    |
                  </span>
                  <button
                    type='button'
                    onClick={collapseAllAccordions}
                    className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 rounded-sm'
                  >
                    Collapse all
                  </button>
                </div>
                <div className='space-y-5'>
                {fundingTypesToRender.length === 0 && hasActiveFilters && (
                  <div className='rounded-xl border border-dashed border-gray-300 bg-gray-50/80 px-6 py-12 text-sm text-gray-600 text-center leading-relaxed'>
                    No line items match the current filters. Try adjusting your search or use Clear filters to reset.
                  </div>
                )}
                {fundingTypesToRender.map(({ value, label }) => {
                  const catsToShow = catsToShowForFunding(value);
                  const hasAny = catsToShow.length > 0;
                  let totalItems = catsToShow.reduce((sum, sc) => sum + (itemsByCategoryId[sc.id] || []).length, 0);
                  if (value === "NDIS") {
                    const systemTotal = (systemCatalogueByCategory || []).reduce((sum, g) => {
                      const filteredPace = filterPaceCategories(g.pace_categories || []);
                      const paceTotal = filteredPace.reduce(
                        (paceSum, paceCat) => paceSum + (paceCat.items?.length || 0),
                        0
                      );
                      return sum + paceTotal;
                    }, 0);
                    totalItems += systemTotal;
                  }
                  const isFundingExpanded = expandedFundingTypes.has(value);
                  const rowColors = fundingTypeRowColors(value);
                  return (
                    <div
                      key={value}
                      className={`rounded-2xl border border-gray-200 bg-white overflow-hidden transition-colors duration-200 border-l-[3px] ${
                        isFundingExpanded ? "border-l-blue-dark" : "border-l-gray-300"
                      }`}
                    >
                      <div
                        role='button'
                        tabIndex={0}
                        onClick={() => toggleFundingType(value)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleFundingType(value); } }}
                        className={`flex flex-wrap items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 cursor-pointer select-none rounded-t-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-dark/20 ${rowColors.row}`}
                        aria-expanded={isFundingExpanded}
                      >
                        <div className='flex items-center gap-4 min-w-0 flex-1'>
                          <span className={`inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-xl text-sm font-bold tracking-tight ${rowColors.badge}`}>
                            {label.charAt(0)}
                          </span>
                          <div className='min-w-0'>
                            <span className='block text-[15px] font-semibold text-slate-900 tracking-tight'>{label}</span>
                            <span className='block text-xs text-slate-500 mt-1 font-medium'>
                              {catsToShow.length} service type{catsToShow.length !== 1 ? "s" : ""} · {totalItems} line item{totalItems !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                        <div className='flex items-center gap-3 shrink-0'>
                          <span className='text-slate-400' aria-hidden>
                            <svg
                              className={`w-5 h-5 transition-transform duration-200 ease-out ${isFundingExpanded ? "rotate-180" : "-rotate-90"}`}
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                            </svg>
                          </span>
                          {value !== "NDIS" && (
                            <CustomButton
                              variant='secondary'
                              label='Add service type'
                              onClick={(e) => { e.stopPropagation(); openAddCategory(value); }}
                              cssClass='!text-xs !py-2 !px-3 !normal-case'
                            />
                          )}
                        </div>
                      </div>
                      {isFundingExpanded && (
                        <div className='space-y-4 px-4 sm:px-5 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50'>

                          {/* ── NDIS: 3-level hierarchy (budget type → category → items) ── */}
                          {value === "NDIS" && ndisBudgetTypes.map((bt) => {
                            const btKey = budgetTypeKey(bt);
                            const isBtExpanded = expandedBudgetTypes.has(btKey);
                            const catsInBt = catsToShow.filter((sc) => sc.ndis_budget_type_id === bt.id);
                            const systemCatData = getSystemCatalogueDataForCategory(bt.id);
                            const allPaceCategories = systemCatData?.pace_categories || [];
                            const paceCategories = filterPaceCategories(allPaceCategories);
                            const orphanPaceCategories = getOrphanPaceCategories(catsInBt, paceCategories);
                            if (!ndisBudgetTypeHasVisibleContent(bt, catsInBt, paceCategories)) {
                              return null;
                            }
                            const systemItemsCount = paceCategories.reduce(
                              (s, paceCat) => s + (paceCat.items?.length || 0),
                              0
                            );
                            const btCustomCount = catsInBt.reduce((s, sc) => s + (itemsByCategoryId[sc.id] || []).length, 0);
                            const btTotalItems = systemItemsCount + btCustomCount;
                            const btDisplayName = toDisplayLabel(bt.name);
                            const btColors = {
                              Core: "bg-blue-100 text-blue-900 ring-blue-300",
                              Capital: "bg-purple-100 text-purple-900 ring-purple-300",
                              "Capacity Building": "bg-emerald-100 text-emerald-900 ring-emerald-300",
                            };
                            const btTone = btColors[btDisplayName] || "bg-slate-100 text-slate-800 ring-slate-300";
                            const btAvatarClass = `ring-1 ring-inset ${btTone}`;
                            const btLabelBadgeClass = `inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide rounded-md ring-1 ring-inset ${btTone}`;
                            return (
                              <div key={bt.id} className={`rounded-xl border border-gray-200 bg-white overflow-hidden border-l-[3px] transition-colors duration-200 ${isBtExpanded ? "border-l-blue-600" : "border-l-gray-300 hover:border-gray-400"}`}>
                                <div
                                  role='button' tabIndex={0}
                                  onClick={() => toggleBudgetType(btKey)}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleBudgetType(btKey); }}}
                                  className='flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 cursor-pointer select-none bg-white hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/25'
                                  aria-expanded={isBtExpanded}
                                >
                                  <div className='flex items-center gap-3 min-w-0 flex-1'>
                                    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold ${btAvatarClass}`}>
                                      {btDisplayName.charAt(0)}
                                    </span>
                                    <div className='min-w-0'>
                                      <div className='flex flex-wrap items-center gap-2'>
                                        <span className='text-[15px] font-semibold text-slate-900 tracking-tight'>{btDisplayName}</span>
                                        <span className={btLabelBadgeClass}>NDIS Budget Type</span>
                                      </div>
                                       <p className='text-xs text-slate-500 mt-0.5'>{catsInBt.length} service type{catsInBt.length !== 1 ? "s" : ""} · {btTotalItems} item{btTotalItems !== 1 ? "s" : ""}</p>
                                    </div>
                                  </div>
                                  <div className='flex items-center gap-2 sm:gap-3 shrink-0'>
                                    <span className='text-slate-400' aria-hidden>
                                      <svg className={`w-5 h-5 transition-transform duration-200 ease-out ${isBtExpanded ? "rotate-180" : "-rotate-90"}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                      </svg>
                                    </span>
                                    <div className='flex items-center gap-2' onClick={(e) => e.stopPropagation()}>
                                      <CustomButton variant='secondary' label='Add service type' onClick={() => openAddCategory("NDIS", bt.id)} cssClass='!text-xs !py-1.5 !px-2.5 !normal-case' />
                                    </div>
                                  </div>
                                </div>
                                {isBtExpanded && (
                                  <div className='border-t border-gray-100 bg-gray-50/40 p-4 space-y-3'>
                                    {catsInBt.map((sc) => {
                                      const categoryItems = itemsByCategoryId[sc.id] || [];
                                      const serviceTypeNameMatches = Boolean(
                                        appliedSearchTerm &&
                                          String(sc?.name || "")
                                            .toLowerCase()
                                            .includes(appliedSearchTerm)
                                      );
                                      const rawCatalogueItems = getCatalogueItemsForServiceType(sc, allPaceCategories);
                                      const catalogueItems = serviceTypeNameMatches
                                        ? rawCatalogueItems
                                        : filterCatalogueItems(rawCatalogueItems);
                                      const catalogueItemsCount = catalogueItems.length;
                                      const catKey = categoryKey(sc);
                                      const isCatExpanded = expandedCategories.has(catKey);
                                      return (
                                        <div key={sc.uuid || sc.id} className={`rounded-lg border border-gray-200 bg-white overflow-hidden transition-colors duration-200 ${isCatExpanded ? "ring-1 ring-blue-100" : "hover:border-gray-300"}`}>
                                          <div role='button' tabIndex={0} onClick={() => toggleCategory(catKey)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCategory(catKey); }}}
                                            className='flex flex-wrap items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500/25' aria-expanded={isCatExpanded}>
                                            <div className='flex items-center gap-2.5 min-w-0 flex-1'>
                                              <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold ${sc.is_system ? "bg-slate-200 text-slate-800 ring-1 ring-inset ring-slate-300" : "bg-blue-100 text-blue-900 ring-1 ring-inset ring-blue-300"}`}>
                                                {(sc.name || "?").trim().charAt(0).toUpperCase()}
                                              </span>
                                              <div className='min-w-0'>
                                                <div className='flex flex-wrap items-center gap-2'>
                                                  <span className='text-sm font-semibold text-slate-900'>{toDisplayLabel(sc.name)}</span>
                                                  <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide rounded-md ring-1 ring-inset ${sc.is_system ? "bg-slate-200 text-slate-800 ring-slate-300" : "bg-emerald-100 text-emerald-900 ring-emerald-300"}`}>{sc.is_system ? "System" : "Custom"}</span>
                                                </div>
                                                <span className='block text-[10px] text-slate-500'>{categoryItems.length} custom · {catalogueItemsCount} read-only</span>
                                              </div>
                                            </div>
                                            <div className='flex items-center gap-2 sm:gap-3 shrink-0'>
                                              <span className='text-slate-400' aria-hidden>
                                                <svg className={`w-5 h-5 transition-transform duration-200 ease-out ${isCatExpanded ? "rotate-180" : "-rotate-90"}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                                </svg>
                                              </span>
                                              <div className='flex items-center gap-2 flex-wrap justify-end' onClick={(e) => e.stopPropagation()}>
                                                {!sc.is_system && (
                                                  <>
                                                    <button type='button' onClick={() => openEditCategory(sc)} className='text-[10px] font-semibold text-slate-600 hover:text-blue-700 rounded px-1.5 py-1 hover:bg-slate-100'>Edit</button>
                                                    <button type='button' onClick={() => openDeleteCategory(sc)} className='text-[10px] font-semibold text-rose-600 hover:text-rose-700 rounded px-1.5 py-1 hover:bg-rose-50'>Delete</button>
                                                  </>
                                                )}
                                                <CustomButton variant='secondary' label='Add custom item' onClick={() => openCreate("NDIS", sc.id)} cssClass='!text-xs !py-1.5 !px-2.5 !normal-case' />
                                              </div>
                                            </div>
                                          </div>
                                          {isCatExpanded && (
                                            <div className='border-t border-gray-200 bg-gray-50/30 p-3 space-y-3'>
                                              <div className='rounded-lg border border-slate-200 bg-white overflow-hidden'>
                                                <div className='flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200'>
                                                  <span className='inline-flex h-5 w-5 items-center justify-center rounded bg-blue-600 text-white shrink-0'>
                                                    <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'><path fillRule='evenodd' d='M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z' clipRule='evenodd' /></svg>
                                                  </span>
                                                  <span className='text-[11px] font-bold uppercase tracking-wider text-slate-700 flex-1'>Custom line items</span>
                                                  <span className='px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-blue-600 text-white'>Custom line items</span>
                                                </div>
                                                {categoryItems.length === 0 ? (
                                                  <div className='px-5 py-8 text-center'>
                                                    <p className='text-sm text-slate-500'>No custom line items yet.</p>
                                                  </div>
                                                ) : (
                                                  <div className='overflow-x-auto'>
                                                    <table className='min-w-full divide-y divide-slate-100'>
                                                      <thead className='bg-slate-50/95'><tr>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Code</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Description</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Rate</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Status</th>
                                                        <th className='px-4 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Actions</th>
                                                      </tr></thead>
                                                      <tbody className='divide-y divide-slate-100 bg-white'>
                                                        {categoryItems.map((row) => (
                                                          <tr key={row.uuid} className={`even:bg-slate-50/35 hover:bg-slate-100/55 ${isRowActionLoading(row) ? "bg-blue-50/40" : ""}`}>
                                                            <td className='px-4 py-3 text-xs font-semibold text-slate-900 tabular-nums'>{row.unique_code}</td>
                                                            <td className='px-4 py-3 text-xs text-slate-600 leading-snug max-w-xs'>{row.description}</td>
                                                            <td className='px-4 py-3 text-xs font-semibold tabular-nums text-slate-800'>${Number(row.rate).toFixed(2)}</td>
                                                            <td className='px-4 py-3'>
                                                              {renderLineItemStatus(row)}
                                                            </td>
                                                            <td className='px-4 py-3 text-right'>
                                                              <span className='inline-flex items-center gap-1'>
                                                                <button type='button' onClick={() => openEdit(row)} disabled={isRowActionLoading(row)} className='text-[10px] font-semibold text-slate-600 hover:text-blue-700 rounded px-1.5 py-1 hover:bg-slate-100 disabled:opacity-50'>Edit</button>
                                                                <span className='text-slate-300' aria-hidden>·</span>
                                                                {renderLineItemStatusToggle(row)}
                                                                <span className='text-slate-300' aria-hidden>·</span>
                                                                <button type='button' onClick={() => handleDelete(row.uuid, row.unique_code)} disabled={isRowActionLoading(row) || !!itemToDelete} className='text-[10px] font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50 rounded px-1.5 py-1 hover:bg-rose-50'>Delete</button>
                                                              </span>
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                )}
                                              </div>
                                              {catalogueItemsCount > 0 && (
                                                <div className='rounded-lg border border-slate-200 bg-white overflow-hidden'>
                                                  <div className='flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200'>
                                                    <span className='inline-flex h-5 w-5 items-center justify-center rounded bg-slate-600 text-white shrink-0'>
                                                      <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'><path fillRule='evenodd' d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z' clipRule='evenodd' /></svg>
                                                    </span>
                                                    <span className='text-[11px] font-bold uppercase tracking-wider text-slate-700 flex-1'>NDIS government price catalogue</span>
                                                    <span className='px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-slate-600 text-white'>Read-only</span>
                                                  </div>
                                                  <div className='p-3 space-y-2'>
                                                    <div className='overflow-x-auto rounded border border-gray-200 bg-white'>
                                                      <table className='min-w-full divide-y divide-slate-100'>
                                                        <thead className='bg-slate-50/95'><tr>
                                                          <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Code</th>
                                                          <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Description</th>
                                                          <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Rate</th>
                                                          <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Unit</th>
                                                        </tr></thead>
                                                        <tbody className='divide-y divide-slate-100 bg-white'>
                                                          {catalogueItems.map((row) => (
                                                            <tr key={row.id || row.uuid} className='even:bg-slate-50/35 hover:bg-slate-100/50'>
                                                              <td className='px-4 py-2.5 text-xs font-semibold text-slate-900 tabular-nums'>{row.support_item_number}</td>
                                                              <td className='px-4 py-2.5 text-xs text-slate-600 leading-snug'>{row.support_item_name}</td>
                                                              <td className='px-4 py-2.5 text-xs tabular-nums font-medium text-slate-800'>{row.price != null ? `$${Number(row.price).toFixed(2)}` : "—"}</td>
                                                              <td className='px-4 py-2.5 text-xs text-slate-500'>{row.unit ?? "—"}</td>
                                                            </tr>
                                                          ))}
                                                        </tbody>
                                                      </table>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {orphanPaceCategories.map((paceCat) => {
                                      const paceKey = `pace-${bt.id}-${String(paceCat.pace_category_name || "").trim().toLowerCase()}`;
                                      const isPaceExpanded = expandedCategories.has(paceKey);
                                      const paceItems = paceCat.items || [];
                                      return (
                                        <div key={paceKey} className={`rounded-lg border border-gray-200 bg-white overflow-hidden transition-colors duration-200 ${isPaceExpanded ? "ring-1 ring-blue-100" : "hover:border-gray-300"}`}>
                                          <div
                                            role='button'
                                            tabIndex={0}
                                            onClick={() => toggleCategory(paceKey)}
                                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCategory(paceKey); } }}
                                            className='flex flex-wrap items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500/25'
                                            aria-expanded={isPaceExpanded}
                                          >
                                            <div className='flex items-center gap-2.5 min-w-0 flex-1'>
                                              <span className='inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold bg-slate-200 text-slate-800 ring-1 ring-inset ring-slate-300'>
                                                {String(paceCat.pace_category_name || "?").trim().charAt(0).toUpperCase()}
                                              </span>
                                              <div className='min-w-0'>
                                                <div className='flex flex-wrap items-center gap-2'>
                                                  <span className='text-sm font-semibold text-slate-900'>{toDisplayLabel(paceCat.pace_category_name)}</span>
                                                  <span className='inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide rounded-md ring-1 ring-inset bg-slate-200 text-slate-800 ring-slate-300'>System</span>
                                                </div>
                                                <span className='block text-[10px] text-slate-500'>0 custom · {paceItems.length} read-only</span>
                                              </div>
                                            </div>
                                            <svg className={`w-4 h-4 text-slate-400 pointer-events-none transition-transform duration-200 ${isPaceExpanded ? "rotate-180" : "-rotate-90"}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' /></svg>
                                          </div>
                                          {isPaceExpanded && (
                                            <div className='border-t border-gray-200 bg-gray-50/30 p-3'>
                                              <div className='rounded-lg border border-slate-200 bg-white overflow-hidden'>
                                                <div className='flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200'>
                                                  <span className='inline-flex h-5 w-5 items-center justify-center rounded bg-slate-600 text-white shrink-0'>
                                                    <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'><path fillRule='evenodd' d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z' clipRule='evenodd' /></svg>
                                                  </span>
                                                  <span className='text-[11px] font-bold uppercase tracking-wider text-slate-700 flex-1'>NDIS government price catalogue</span>
                                                  <span className='px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-slate-600 text-white'>Read-only</span>
                                                </div>
                                                <div className='p-3'>
                                                  <div className='overflow-x-auto rounded border border-gray-200 bg-white'>
                                                    <table className='min-w-full divide-y divide-slate-100'>
                                                      <thead className='bg-slate-50/95'><tr>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Code</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Description</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Rate</th>
                                                        <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Unit</th>
                                                      </tr></thead>
                                                      <tbody className='divide-y divide-slate-100 bg-white'>
                                                        {paceItems.map((row) => (
                                                          <tr key={row.id || row.uuid} className='even:bg-slate-50/35 hover:bg-slate-100/50'>
                                                            <td className='px-4 py-2.5 text-xs font-semibold text-slate-900 tabular-nums'>{row.support_item_number}</td>
                                                            <td className='px-4 py-2.5 text-xs text-slate-600 leading-snug'>{row.support_item_name}</td>
                                                            <td className='px-4 py-2.5 text-xs tabular-nums font-medium text-slate-800'>{row.price != null ? `$${Number(row.price).toFixed(2)}` : "—"}</td>
                                                            <td className='px-4 py-2.5 text-xs text-slate-500'>{row.unit ?? "—"}</td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {catsInBt.length === 0 && orphanPaceCategories.length === 0 && (
                                      <div className='rounded border border-dashed border-gray-300 bg-white px-5 py-8 text-center'>
                                        <p className='text-sm text-slate-500 mb-3'>No service types yet.</p>
                                        <CustomButton variant='secondary' label='Add service type' onClick={() => openAddCategory("NDIS", bt.id)} cssClass='!text-xs !py-1.5 !px-2.5 !normal-case' />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Ungrouped NDIS service types (user-created without a budget type) */}
                          {value === "NDIS" && catsToShow.filter((sc) => !sc.ndis_budget_type_id).length > 0 && (
                            <div className='rounded-xl border border-gray-200 bg-white overflow-hidden border-l-[3px] border-l-amber-400'>
                              <div className='px-4 sm:px-5 py-3.5 bg-amber-50/50 border-b border-amber-100/80'>
                                <span className='text-[10px] font-bold uppercase tracking-wider text-amber-800'>Custom service types (no budget type assigned)</span>
                                <p className='text-xs text-amber-700/80 mt-0.5'>These service types were created without a budget type. Consider reassigning them.</p>
                              </div>
                              <div className='p-4 space-y-2'>
                                {catsToShow.filter((sc) => !sc.ndis_budget_type_id).map((sc) => {
                                  const catKey = categoryKey(sc);
                                  const isCatExpanded = expandedCategories.has(catKey);
                                  const categoryItems = itemsByCategoryId[sc.id] || [];
                                  return (
                                    <div key={sc.uuid || sc.id} className={`rounded-lg border border-gray-200 bg-white overflow-hidden border-l-[2px] transition-colors duration-200 ${isCatExpanded ? "border-l-amber-500" : "border-l-gray-200"}`}>
                                      <div role='button' tabIndex={0} onClick={() => toggleCategory(catKey)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCategory(catKey); }}}
                                        className='flex flex-wrap items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none bg-white hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500/25' aria-expanded={isCatExpanded}>
                                        <div className='flex items-center gap-2.5 min-w-0 flex-1'>
                                          <span className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-300'>{(sc.name || "?").charAt(0).toUpperCase()}</span>
                                          <div className='min-w-0'>
                                            <span className='text-sm font-semibold text-slate-900'>{toDisplayLabel(sc.name)}</span>
                                            <p className='text-xs text-slate-500 mt-0.5'>{categoryItems.length} line item{categoryItems.length !== 1 ? "s" : ""}</p>
                                          </div>
                                        </div>
                                        <div className='flex items-center gap-2 sm:gap-3 shrink-0'>
                                          <span className='text-slate-400' aria-hidden>
                                            <svg className={`w-5 h-5 transition-transform duration-200 ease-out ${isCatExpanded ? "rotate-180" : "-rotate-90"}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                            </svg>
                                          </span>
                                          <div className='flex items-center gap-2 flex-wrap justify-end' onClick={(e) => e.stopPropagation()}>
                                            {!sc.is_system && (
                                              <>
                                                <button type='button' onClick={() => openEditCategory(sc)} className='text-[10px] font-semibold text-slate-600 hover:text-blue-700 rounded px-1.5 py-1 hover:bg-slate-100'>Edit</button>
                                                <button type='button' onClick={() => openDeleteCategory(sc)} className='text-[10px] font-semibold text-rose-600 hover:text-rose-700 rounded px-1.5 py-1 hover:bg-rose-50'>Delete</button>
                                              </>
                                            )}
                                            <CustomButton variant='secondary' label='Add item' onClick={() => openCreate("NDIS", sc.id)} cssClass='!text-xs !py-1.5 !px-2.5 !normal-case' />
                                          </div>
                                        </div>
                                      </div>
                                      {isCatExpanded && (
                                        <div className='border-t border-gray-100 bg-gray-50/30'>
                                          {categoryItems.length === 0 ? (
                                            <div className='px-5 py-8 text-center'><p className='text-sm text-slate-500'>No line items yet. Use <strong>Add item</strong> to create one.</p></div>
                                          ) : (
                                            <div className='overflow-x-auto'>
                                              <table className='min-w-full divide-y divide-slate-100'>
                                                <thead className='bg-slate-50/95'><tr>
                                                  <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Code</th>
                                                  <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Description</th>
                                                  <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Rate</th>
                                                  <th className='px-4 py-2.5 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Status</th>
                                                  <th className='px-4 py-2.5 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider'>Actions</th>
                                                </tr></thead>
                                                <tbody className='divide-y divide-slate-100 bg-white'>
                                                  {categoryItems.map((row) => (
                                                    <tr key={row.uuid} className={`even:bg-slate-50/35 hover:bg-slate-100/55 ${isRowActionLoading(row) ? "bg-blue-50/40" : ""}`}>
                                                      <td className='px-4 py-3 text-xs font-semibold text-slate-900 tabular-nums'>{row.unique_code}</td>
                                                      <td className='px-4 py-3 text-xs text-slate-600 leading-snug max-w-xs'>{row.description}</td>
                                                      <td className='px-4 py-3 text-xs font-semibold tabular-nums text-slate-800'>${Number(row.rate).toFixed(2)}</td>
                                                      <td className='px-4 py-3'>{renderLineItemStatus(row)}</td>
                                                      <td className='px-4 py-3 text-right'>
                                                        <span className='inline-flex items-center gap-1'>
                                                          <button type='button' onClick={() => openEdit(row)} disabled={isRowActionLoading(row)} className='text-[10px] font-semibold text-slate-600 hover:text-blue-700 rounded px-1.5 py-1 hover:bg-slate-100 disabled:opacity-50'>Edit</button>
                                                          <span className='text-slate-300' aria-hidden>·</span>
                                                          {renderLineItemStatusToggle(row)}
                                                          <span className='text-slate-300' aria-hidden>·</span>
                                                          <button type='button' onClick={() => handleDelete(row.uuid, row.unique_code)} disabled={isRowActionLoading(row) || !!itemToDelete} className='text-[10px] font-semibold text-rose-600 disabled:opacity-50 rounded px-1.5 py-1 hover:bg-rose-50'>Delete</button>
                                                        </span>
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* ── Non-NDIS: flat 2-level (category → items) – unchanged ── */}
                          {value !== "NDIS" && catsToShow.map((sc) => {
                            const categoryItems = itemsByCategoryId[sc.id] || [];
                            const totalInCategory = categoryItems.length;
                            const catKey = categoryKey(sc);
                            const isCatExpanded = expandedCategories.has(catKey);
                            return (
                              <div
                                key={sc.uuid || sc.id}
                                className={`rounded-xl border border-gray-200 bg-white overflow-hidden border-l-[3px] transition-colors duration-200 ${
                                  isCatExpanded ? "border-l-blue-dark" : "border-l-gray-300 hover:border-gray-300"
                                }`}
                              >
                                <div
                                  role='button'
                                  tabIndex={0}
                                  onClick={() => toggleCategory(catKey)}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleCategory(catKey); } }}
                                  className={`flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-4 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/25 ${
                                    isCatExpanded
                                      ? "border-b-0 bg-white"
                                      : "border-b border-transparent bg-white hover:bg-gray-50"
                                  }`}
                                  aria-expanded={isCatExpanded}
                                >
                                  <div className='flex items-start gap-3 min-w-0 flex-1'>
                                    <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold ${
                                      sc.is_system
                                        ? "bg-slate-100/90 text-slate-600 ring-1 ring-inset ring-slate-200/60"
                                        : "bg-blue-dark/[0.06] text-blue-dark ring-1 ring-inset ring-blue-dark/12"
                                    }`}>
                                      {(sc.name || "?").trim().charAt(0).toUpperCase()}
                                    </span>
                                    <div className='min-w-0'>
                                       <div className='flex flex-wrap items-center gap-2'>
                                        <span className='text-[15px] font-semibold text-slate-900 tracking-tight'>{toDisplayLabel(sc.name)}</span>
                                        <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide rounded-md ring-1 ring-inset ${sc.is_system ? "bg-slate-200 text-slate-800 ring-slate-300" : "bg-emerald-100 text-emerald-900 ring-emerald-300"}`}>
                                          {sc.is_system ? "System" : "Custom"}
                                        </span>
                                       </div>
                                       <p className='text-xs text-slate-500 mt-1 leading-snug'>
                                          {totalInCategory} line item{totalInCategory !== 1 ? "s" : ""} in this service type
                                       </p>
                                     </div>
                                   </div>
                                   <div className='flex items-center gap-2 sm:gap-3 shrink-0'>
                                     <span className='text-slate-400' aria-hidden>
                                       <svg className={`w-5 h-5 transition-transform duration-200 ease-out ${isCatExpanded ? "rotate-180" : "-rotate-90"}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                         <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                       </svg>
                                     </span>
                                     <div className='flex items-center gap-2 flex-wrap justify-end' onClick={(e) => e.stopPropagation()}>
                                       {!sc.is_system && (
                                         <span className='inline-flex items-center gap-2 mr-1'>
                                           <button type='button' onClick={() => openEditCategory(sc)} className='text-[11px] font-semibold text-slate-600 hover:text-blue-dark rounded-md px-1.5 py-1 hover:bg-slate-100/80 transition-colors'>Edit</button>
                                           <button type='button' onClick={() => openDeleteCategory(sc)} className='text-[11px] font-semibold text-rose-600/85 hover:text-rose-700 rounded-md px-1.5 py-1 hover:bg-rose-50/60 transition-colors'>Delete</button>
                                           <span className='hidden sm:inline h-4 w-px bg-slate-200' aria-hidden />
                                         </span>
                                       )}
                                       <CustomButton variant='secondary' label='Add line item' onClick={() => openCreate(value, sc.id)} cssClass='!text-xs !py-2 !px-3 !normal-case' />
                                     </div>
                                   </div>
                                 </div>
                                 {isCatExpanded && (
                                   <div className='min-w-full border-t-2 border-blue-dark/25 bg-gray-50/40 pt-5 sm:pt-6'>
                                     {categoryItems.length === 0 ? (
                                       <div className='mx-4 sm:mx-5 mb-4 rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center'>
                                         <p className='text-sm text-slate-600 leading-relaxed max-w-sm mx-auto'>
                                           No line items yet. Use <span className='font-semibold text-slate-800'>Add line item</span> to create one.
                                         </p>
                                       </div>
                                     ) : (
                                       <div className='overflow-x-auto px-4 pt-2 pb-4 sm:px-5'>
                                         <div className='rounded-lg overflow-hidden border border-gray-200 bg-white'>
                                           <table className='min-w-full divide-y divide-slate-100'>
                                             <thead className='bg-slate-50/95'><tr>
                                               <th className='px-5 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider'>Code</th>
                                               <th className='px-5 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider'>Description</th>
                                               <th className='px-5 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider'>Rate</th>
                                               <th className='px-5 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider'>Status</th>
                                               <th className='px-5 py-3 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider'>Actions</th>
                                             </tr></thead>
                                             <tbody className='divide-y divide-slate-100 bg-white'>
                                               {categoryItems.map((row) => (
                                                 <tr key={row.uuid} className={`even:bg-slate-50/35 hover:bg-slate-100/55 transition-colors duration-150 ${isRowActionLoading(row) ? "bg-blue-50/40" : ""}`}>
                                                   <td className='px-5 py-4 text-sm font-semibold text-slate-900 align-top tabular-nums'>{row.unique_code}</td>
                                                   <td className='px-5 py-4 text-sm text-slate-600 align-top leading-relaxed max-w-md'>{row.description}</td>
                                                   <td className='px-5 py-4 text-sm font-semibold tabular-nums text-slate-800 align-top'>${Number(row.rate).toFixed(2)}</td>
                                                   <td className='px-5 py-4 align-top'>
                                                     {renderLineItemStatus(row)}
                                                   </td>
                                                   <td className='px-5 py-4 text-right align-top'>
                                                     <span className='inline-flex flex-wrap items-center gap-x-1 gap-y-1 justify-end'>
                                                       <button type='button' onClick={() => openEdit(row)} disabled={isRowActionLoading(row)} className='text-[11px] font-semibold text-slate-600 hover:text-blue-dark rounded-lg px-2 py-1.5 hover:bg-slate-100 transition-colors duration-150 disabled:opacity-50'>Edit</button>
                                                       <span className='text-slate-300 select-none' aria-hidden>·</span>
                                                       {renderLineItemStatusToggle(row, "lg")}
                                                       <span className='text-slate-300 select-none' aria-hidden>·</span>
                                                       <button type='button' onClick={() => handleDelete(row.uuid, row.unique_code)} disabled={isRowActionLoading(row) || !!itemToDelete} className='text-[11px] font-semibold text-rose-600/90 hover:text-rose-700 disabled:opacity-50 rounded-lg px-2 py-1.5 hover:bg-rose-50/60 transition-colors duration-150'>Delete</button>
                                                     </span>
                                                   </td>
                                                 </tr>
                                               ))}
                                             </tbody>
                                           </table>
                                         </div>
                                       </div>
                                     )}
                                   </div>
                                 )}
                               </div>
                             );
                           })}
                        {!hasAny && value !== "NDIS" && (
                          <div className='rounded-lg border border-dashed border-gray-300 bg-gray-50/80 px-6 py-12 text-sm text-gray-600 text-center leading-relaxed'>
                            {categoryFilter
                              ? `No matching service type in ${label}. Try a different filter.`
                              : `No service types for ${label}. Add a service type above to start adding line items.`}
                          </div>
                        )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            )}
            </div>
          </section>
        </main>
      </div>

      <ServiceLineItemModal
        isOpen={modalOpen}
        onClose={closeModal}
        editingItem={editingItem}
        submitting={submitting}
        submitError={submitError}
        fieldErrors={fieldErrors}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmitForm}
        register={register}
        setValue={setValue}
        watchRate={watchRate}
        fundingTypes={FUNDING_TYPES}
        formFundingTypeSelectedOption={formFundingTypeSelectedOption}
        formCategoryDropdownData={formCategoryDropdownData}
        formCategorySelectedOption={formCategorySelectedOption}
      />

      <ServiceTypeModal
        isOpen={categoryModalOpen}
        onClose={closeCategoryModal}
        editingCategory={editingCategory}
        categoryName={categoryName}
        setCategoryName={setCategoryName}
        categoryFundingType={categoryFundingType}
        setCategoryFundingType={setCategoryFundingType}
        categoryBudgetTypeId={categoryBudgetTypeId}
        setCategoryBudgetTypeId={setCategoryBudgetTypeId}
        categoryFieldErrors={categoryFieldErrors}
        categorySubmitError={categorySubmitError}
        setCategoryFieldErrors={setCategoryFieldErrors}
        categorySubmitting={categorySubmitting}
        onSubmit={onSubmitCategory}
        fundingTypes={FUNDING_TYPES}
        ndisBudgetTypes={ndisBudgetTypes}
        fundingTypeLabel={fundingTypeLabel}
      />

      <ConfirmationModal
        showModal={!!itemToDelete}
        closeModalHandler={closeDeleteModal}
        submitHandler={handleDeleteConfirm}
        title='Delete service line item'
        heading={itemToDelete ? `Delete "${itemToDelete.uniqueCode}"? This will fail if the item is in use.` : "Delete this item?"}
        isNew={true}
        cancelBtnLabel='Cancel'
        confirmBtnLabel='Delete'
        variantConfirmBtn='danger'
        confirmBtnLoader={deleting}
      />

      <ConfirmationModal
        showModal={!!categoryToDelete}
        closeModalHandler={closeDeleteCategoryModal}
        submitHandler={handleDeleteCategoryConfirm}
        title='Delete service type'
        isNew={true}
        cancelBtnLabel='Cancel'
        confirmBtnLabel='Delete'
        variantConfirmBtn='danger'
        confirmBtnLoader={deletingCategory}
        confirmBtnDisabled={!!categoryToDelete && !canDeleteServiceCategory(categoryToDelete)}
      >
        {categoryToDelete?.deleteCheckPending ? (
          <p className='text-sm leading-relaxed text-gray-700'>Checking whether this service type can be deleted...</p>
        ) : isServiceCategoryDeleteBlocked(categoryToDelete) ? (
          <div className='rounded-md bg-red-50 border border-red-100 p-3'>
            <p className='text-sm leading-relaxed text-red-700'>{SERVICE_CATEGORY_IN_USE_DELETE_MSG}</p>
          </div>
        ) : (
          <p className='text-sm leading-relaxed text-gray-700'>
            {categoryToDelete
              ? `Delete "${categoryToDelete.name}"? Associated unused line items will also be removed.`
              : "Delete this service type?"}
          </p>
        )}
      </ConfirmationModal>
    </>
  );
};

export default CustomLineItems;
